import { MantineProvider } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import React from "react";

import { Pagination } from "../../components/Pagination";
import { RpiTableContainer } from "../../components/RpiTableContainer";
import { Heading } from "../../components/Typography";
import { usePageShellStyles } from "../../components/pageShellStyles";
import { RpiSearchFilter } from "../../components/search-filter/RpiSearchFilter";
import { SearchFilterBar } from "../../components/search-filter/SearchFilterBar";
import { printAssetColumns } from "../../configuration/columns/printAsset.column";
import { usePrintAssetFilters } from "../../configuration/filters/printAsset.filter";
import { SearchInput, toSearchInputArray } from "../../configuration/filters/searchInput";
import { featureFlags } from "../../featureFlags";
import { useRpiPagination } from "../../hooks/usePagination";
import { RpiTable } from "../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../theme/rpaTheme";
import { SearchGeneral, SearchSort } from "../../utils/graphql.util";
import { printAssetSearchLabels, printAssetSearchRenderers } from "../../utils/searchField.util";
import { PrintAssetsFilter } from "./PrintAssetsFilter";

export const PrintAssetsView: React.FC = () => {
  const { classes } = usePageShellStyles();
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.PrintAssetsQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.PrintAssetSearchFields>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.PrintAssetsQueryVariables>>();

  const searchFilters = toSearchInputArray(searchInputs);
  const setSearchFilters = (
    recipe: (filters: SearchInput<GraphQL.PrintAssetSearchFields>[]) => SearchInput<GraphQL.PrintAssetSearchFields>[]
  ) => {
    setSearchInputs((prev) => recipe(toSearchInputArray(prev)));
  };

  const pagination = useRpiPagination({ perPage: 20 });

  const printAssetFilters = usePrintAssetFilters();

  const handleSearchSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
    pagination.firstPage();
  };

  const { data, isLoading, error } = GraphQL.usePrintAssetsQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.printAssets.totalCount),
    }
  );
  const printAssets = data?.printAssets.data;

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="files-view" aria-label="Files view">
        <Heading as="h1" size="6xl">
          Files
        </Heading>
        <div className={classes.row}>
          {featureFlags.newSearchExperience ? (
            <SearchFilterBar
              placeholder="Search by Upload ID"
              isLoading={isLoading}
              filters={searchFilters}
              onSetFilters={setSearchFilters}
              onSearchSubmit={handleSearchSubmit}
              onFilterClick={() => setIsFilterOpen(true)}
              filterLabels={printAssetSearchLabels}
              valueRenderers={printAssetSearchRenderers}
            />
          ) : (
            <RpiSearchFilter
              placeholder="Search by Upload ID"
              filters={printAssetFilters}
              dateRangeField={GraphQL.PrintAssetSearchFields.UpdatedAt}
              onChange={setSearchInputs}
              onSearch={setSearchGeneral}
            />
          )}
        </div>
        <RpiTableContainer
          data-testid="print-assets-table"
          footer={<Pagination isLoading={isLoading} pagination={pagination} label="Files" />}
        >
          <RpiTable
            emptyMessage={searchInputs?.length || searchGeneral ? "No files found." : "Files will appear here."}
            isLoading={isLoading}
            error={error}
            perPage={printAssets?.length}
            data={printAssets}
            columns={printAssetColumns}
            withSortBy={{ onChange: setSortInput }}
          />
        </RpiTableContainer>
      </div>
      {featureFlags.newSearchExperience && (
        <PrintAssetsFilter
          isOpened={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={searchFilters}
          onApply={setSearchInputs}
        />
      )}
    </MantineProvider>
  );
};
